export const pharmacologyModule = {
  id: "pharmacology",
  title: "Adrenal Gland: Pharmacology",
  sections: [
    {
      type: "text",
      content: `
        <h3>Overview: Adrenal Pharmacology</h3>
        <p>Drugs acting on the adrenal gland target four main systems:</p>
        <ul>
          <li><b class="purple-term">Glucocorticoids</b> — synthetic cortisol analogues used as anti-inflammatory and immunosuppressive agents</li>
          <li><b class="purple-term">Mineralocorticoids and their antagonists</b> — fludrocortisone (replacement), spironolactone and eplerenone (aldosterone blockers)</li>
          <li><b class="purple-term">Steroidogenesis inhibitors</b> — drugs that block cortisol synthesis, used in Cushing's syndrome</li>
          <li><b class="purple-term">Phaeochromocytoma drugs</b> — alpha and beta blockers used to control catecholamine excess before surgical resection</li>
        </ul>
        <div class="highlight-box">
          Understanding adrenal pharmacology requires knowing which step in each pathway is being targeted, and what happens when that step is blocked or mimicked.
        </div>
      `,
    },
    {
      type: "text",
      content: `
        <h3>Glucocorticoid Drugs</h3>
        <p>Synthetic glucocorticoids mimic the actions of endogenous cortisol at glucocorticoid receptors (GRs). They vary in their <b>potency</b>, <b>duration of action</b>, and degree of unwanted <b>mineralocorticoid activity</b> (Na⁺ retention, K⁺ loss).</p>

        <h3>Steroid equivalence table</h3>
        <p>All doses below are equivalent anti-inflammatory doses compared to <b>hydrocortisone 20 mg</b> as the reference:</p>
        <table style="width:100%; border-collapse: collapse; font-size: 0.9em; margin-top: 8px;">
          <tr style="background:#f3f4f6">
            <th style="padding:5px 8px; text-align:left">Drug</th>
            <th style="padding:5px 8px">Equivalent dose</th>
            <th style="padding:5px 8px">Glucocorticoid potency</th>
            <th style="padding:5px 8px">Mineralocorticoid activity</th>
            <th style="padding:5px 8px">Duration</th>
          </tr>
          <tr>
            <td style="padding:5px 8px"><b>Hydrocortisone</b></td>
            <td style="padding:5px 8px; text-align:center">20 mg</td>
            <td style="padding:5px 8px; text-align:center">1× (reference)</td>
            <td style="padding:5px 8px; text-align:center">Moderate +++</td>
            <td style="padding:5px 8px; text-align:center">Short (8–12 h)</td>
          </tr>
          <tr style="background:#f9fafb">
            <td style="padding:5px 8px"><b>Prednisolone</b></td>
            <td style="padding:5px 8px; text-align:center">5 mg</td>
            <td style="padding:5px 8px; text-align:center">4×</td>
            <td style="padding:5px 8px; text-align:center">Mild +</td>
            <td style="padding:5px 8px; text-align:center">Intermediate (12–36 h)</td>
          </tr>
          <tr>
            <td style="padding:5px 8px"><b>Methylprednisolone</b></td>
            <td style="padding:5px 8px; text-align:center">4 mg</td>
            <td style="padding:5px 8px; text-align:center">5×</td>
            <td style="padding:5px 8px; text-align:center">Minimal</td>
            <td style="padding:5px 8px; text-align:center">Intermediate (12–36 h)</td>
          </tr>
          <tr style="background:#f9fafb">
            <td style="padding:5px 8px"><b>Dexamethasone</b></td>
            <td style="padding:5px 8px; text-align:center">0.75 mg</td>
            <td style="padding:5px 8px; text-align:center">~30×</td>
            <td style="padding:5px 8px; text-align:center">None</td>
            <td style="padding:5px 8px; text-align:center">Long (36–54 h)</td>
          </tr>
          <tr>
            <td style="padding:5px 8px"><b>Fludrocortisone</b></td>
            <td style="padding:5px 8px; text-align:center">—</td>
            <td style="padding:5px 8px; text-align:center">Weak (~10×)</td>
            <td style="padding:5px 8px; text-align:center">Very strong 125×</td>
            <td style="padding:5px 8px; text-align:center">Long</td>
          </tr>
        </table>
        <div class="highlight-box" style="margin-top:10px">
          <b>Key rule:</b> As glucocorticoid potency increases, mineralocorticoid activity generally decreases.<br/>
          <b>Dexamethasone</b> has NO mineralocorticoid activity → does not cause Na⁺ retention → drug of choice for the <b>dexamethasone suppression test</b> (suppresses ACTH without confounding fluid balance).
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with severe asthma requires a 5-day course of oral steroids. Which steroid is most commonly used, and at what approximate equivalent dose compared to hydrocortisone?",
      options: [
        "Dexamethasone 0.75 mg — 30× more potent than hydrocortisone",
        "Prednisolone 5 mg — 4× more potent than hydrocortisone",
        "Fludrocortisone 0.1 mg — used for its mineralocorticoid properties",
        "Hydrocortisone 20 mg — used as the reference steroid",
      ],
      correct: 1,
      explanation:
        "Prednisolone is the most commonly prescribed oral glucocorticoid for inflammatory conditions (asthma, COPD exacerbations, inflammatory bowel disease). It is approximately 4× more potent than hydrocortisone, so 5 mg prednisolone ≈ 20 mg hydrocortisone. It has minimal mineralocorticoid activity, an intermediate duration of action and good oral bioavailability.",
    },
    {
      type: "question",
      text: "Why is dexamethasone — rather than prednisolone — used in the overnight dexamethasone suppression test for Cushing's syndrome?",
      options: [
        "Dexamethasone has stronger mineralocorticoid activity, causing greater sodium retention",
        "Dexamethasone has no mineralocorticoid activity and does not cross-react with most cortisol immunoassays, allowing accurate cortisol measurement after suppression",
        "Dexamethasone is cleared more slowly than prednisolone, giving a longer duration of HPA suppression",
        "Prednisolone interferes with ACTH release but dexamethasone does not",
      ],
      correct: 1,
      explanation:
        "The overnight dexamethasone suppression test involves giving 1 mg dexamethasone at midnight and measuring cortisol at 9 am. Dexamethasone is used because: (1) it has negligible mineralocorticoid activity; and (2) crucially, it does not cross-react with most cortisol immunoassays — so the measured cortisol reflects only endogenous cortisol, not the administered drug. In a normal person, dexamethasone suppresses ACTH → cortisol falls below 50 nmol/L. Failure to suppress suggests autonomous cortisol production.",
    },
    {
      type: "text",
      content: `
        <h3>Clinical Uses of Glucocorticoids</h3>
        <p>Glucocorticoids are among the most widely prescribed drugs in medicine. Key clinical uses include:</p>
        <ul>
          <li><b>Replacement therapy</b>: hydrocortisone in Addison's disease or secondary adrenal insufficiency (mimics the diurnal pattern — larger morning dose, smaller evening dose)</li>
          <li><b>Anti-inflammatory</b>: asthma, COPD exacerbations, rheumatoid arthritis, inflammatory bowel disease, giant cell arteritis</li>
          <li><b>Immunosuppression</b>: organ transplantation, autoimmune conditions, nephrotic syndrome</li>
          <li><b>Antenatal</b>: betamethasone given to mothers at risk of preterm labour to accelerate fetal lung maturity (induces surfactant production)</li>
          <li><b>Cerebral oedema</b>: dexamethasone reduces vasogenic oedema around brain tumours and abscesses</li>
          <li><b>Septic shock</b>: low-dose hydrocortisone in refractory septic shock (controversial — used when vasopressors insufficient)</li>
          <li><b>Croup</b>: single dose dexamethasone or budesonide (nebulised) reduces subglottic oedema</li>
        </ul>

        <h3>HPA Axis Suppression — The Most Important Side Effect</h3>
        <p>Any glucocorticoid given for <b>more than 3 weeks</b>, or at doses above physiological replacement (<b>&gt;7.5 mg/day prednisolone equivalent</b>), risks suppressing the HPA axis via negative feedback. This causes adrenal atrophy — the glands can no longer mount a cortisol response to stress.</p>
        <p>Consequences:</p>
        <ul>
          <li><b>Abrupt withdrawal</b> of steroids → <b class="purple-term">adrenal crisis</b> (hypotension, hypoglycaemia, shock)</li>
          <li><b>Illness or surgery</b> while on steroids → inadequate stress response → crisis if not supplemented ("sick day rules")</li>
        </ul>
        <div class="mnemonic-card">
          <b>Steroid sick day rules:</b><br/>
          Mild illness (fever, vomiting) → <b>double the dose</b><br/>
          Vomiting / unable to take oral → attend hospital for <b>IV/IM hydrocortisone</b><br/>
          Surgery → <b>perioperative hydrocortisone cover</b> (100 mg IM at induction + regular doses for 24–48 h)
        </div>
        <p>Steroids should be <b>tapered slowly</b> after prolonged use — not stopped abruptly — to allow the HPA axis to recover. Patients on long-term steroids should carry a <b class="purple-term">steroid emergency card</b>.</p>
      `,
    },
    {
      type: "question",
      text: "A patient on prednisolone 20 mg/day for 6 weeks develops gastroenteritis with persistent vomiting and is unable to take their tablets. They present to A&E feeling dizzy and hypotensive. What is the most urgent management?",
      options: [
        "Oral fludrocortisone to replace mineralocorticoid function",
        "IV/IM hydrocortisone 100 mg immediately",
        "Stop the prednisolone as vomiting may be a side effect",
        "Start IV normal saline only and wait for vomiting to resolve",
      ],
      correct: 1,
      explanation:
        "This patient is in adrenal crisis — their HPA axis is suppressed by long-term prednisolone, and vomiting means they cannot absorb oral steroids. The immediate treatment is parenteral hydrocortisone (100 mg IV or IM), which provides both glucocorticoid and mineralocorticoid cover. IV fluid resuscitation is also needed. This is a medical emergency — untreated adrenal crisis is fatal. All patients on long-term steroids should be counselled on sick day rules and carry a steroid emergency card.",
    },
    {
      type: "text",
      content: `
        <h3>Mineralocorticoid Drugs and Aldosterone Antagonists</h3>

        <h3>Fludrocortisone — Mineralocorticoid replacement</h3>
        <p><b class="purple-term">Fludrocortisone</b> is a synthetic mineralocorticoid with approximately <b>125× the mineralocorticoid potency</b> of hydrocortisone and only weak glucocorticoid activity. It acts on renal mineralocorticoid receptors to promote Na⁺ retention and K⁺ excretion.</p>
        <p><b>Clinical uses:</b></p>
        <ul>
          <li><b>Addison's disease</b>: combined with hydrocortisone (which provides the glucocorticoid component); fludrocortisone 50–200 mcg/day replaces the missing aldosterone</li>
          <li><b>Congenital adrenal hyperplasia (CAH)</b>: fludrocortisone used alongside hydrocortisone</li>
          <li><b>Postural hypotension</b>: fludrocortisone increases circulating volume → raises standing BP</li>
        </ul>

        <h3>Spironolactone — Aldosterone antagonist</h3>
        <p><b class="purple-term">Spironolactone</b> is a <b>competitive antagonist at the mineralocorticoid receptor</b> (aldosterone receptor). It blocks the effect of aldosterone on the distal tubule and collecting duct → reduces Na⁺ reabsorption and K⁺ excretion → <b>potassium-sparing diuretic</b>.</p>
        <p><b>Clinical uses:</b></p>
        <ul>
          <li><b>Primary hyperaldosteronism (Conn's syndrome)</b>: medical management when surgery is not possible, or to confirm aldosterone excess (resolution of hypokalaemia on spironolactone supports the diagnosis)</li>
          <li><b>Heart failure</b>: at low doses reduces mortality (RALES trial)</li>
          <li><b>Hypertension</b>: particularly useful in resistant hypertension (4th-line antihypertensive)</li>
          <li><b>Ascites in liver cirrhosis</b>: reduces secondary hyperaldosteronism-driven fluid retention</li>
          <li><b>Acne and hirsutism in women</b>: spironolactone has anti-androgen effects (blocks androgen receptors)</li>
        </ul>
        <div class="highlight-box">
          <b>Side effects of spironolactone:</b> Hyperkalaemia (monitor K⁺) · Gynaecomastia and erectile dysfunction in men (anti-androgen effect) · Menstrual irregularities in women · Metabolic acidosis
        </div>

        <h3>Eplerenone — Selective aldosterone antagonist</h3>
        <p><b class="purple-term">Eplerenone</b> is a more <b>selective</b> mineralocorticoid receptor antagonist than spironolactone. Because it has much lower affinity for androgen and progesterone receptors, it causes <b>fewer hormonal side effects</b> (no gynaecomastia, no menstrual irregularities).</p>
        <p><b>Clinical uses:</b> Heart failure post-myocardial infarction (EPHESUS trial); hypertension; Conn's syndrome (when spironolactone not tolerated).</p>
      `,
    },
    {
      type: "question",
      text: "A male patient with Conn's syndrome is started on spironolactone. After 3 months he returns complaining of breast enlargement and tenderness (gynaecomastia). What is the most appropriate next step?",
      options: [
        "Add fludrocortisone to counteract the anti-aldosterone effect",
        "Switch to eplerenone — a selective aldosterone antagonist with fewer anti-androgen side effects",
        "Increase the spironolactone dose to achieve better aldosterone blockade",
        "Stop all treatment as the gynaecomastia indicates adrenal suppression",
      ],
      correct: 1,
      explanation:
        "Spironolactone is non-selective and also blocks androgen and progesterone receptors, causing gynaecomastia, erectile dysfunction and reduced libido in men. Eplerenone is a selective mineralocorticoid receptor antagonist that lacks significant anti-androgen or anti-progestogenic activity, so it does not cause gynaecomastia. It is the appropriate switch when spironolactone is not tolerated due to hormonal side effects.",
    },
    {
      type: "question",
      text: "Why does spironolactone cause hyperkalaemia, and in which patients is this particularly dangerous?",
      options: [
        "Spironolactone increases ACTH, stimulating zona reticularis androgen production which raises K⁺",
        "Spironolactone blocks aldosterone's action on the distal tubule, reducing K⁺ excretion → K⁺ accumulates in the blood",
        "Spironolactone inhibits Na⁺/K⁺-ATPase in all cells, releasing intracellular K⁺ into the plasma",
        "Spironolactone causes renal tubular acidosis, which shifts K⁺ out of cells",
      ],
      correct: 1,
      explanation:
        "Aldosterone normally stimulates K⁺ secretion into the tubular lumen. By blocking the mineralocorticoid receptor, spironolactone prevents this K⁺ excretion → K⁺ is retained → hyperkalaemia. This is particularly dangerous in patients with: chronic kidney disease (reduced baseline K⁺ excretion), those on ACE inhibitors or ARBs (also raise K⁺), and those with heart failure on digoxin (hyperkalaemia increases digoxin toxicity risk).",
    },
    {
      type: "text",
      content: `
        <h3>Steroidogenesis Inhibitors — Drugs for Cushing's Syndrome</h3>
        <p>These drugs block one or more steps in the cortisol synthesis pathway within the adrenal cortex. They are used when surgical removal of the underlying cause is not possible or has failed.</p>

        <h3>Metyrapone</h3>
        <p><b class="purple-term">Metyrapone</b> inhibits <b class="purple-term">11β-hydroxylase</b> — the enzyme that converts 11-deoxycortisol → cortisol (the final step in cortisol synthesis).</p>
        <ul>
          <li><b>Effect:</b> ↓ Cortisol → ACTH rises (loss of negative feedback) → buildup of 11-deoxycortisol upstream → this is converted to androgens and mineralocorticoid precursors → can cause <b>hirsutism</b> and <b>hypertension</b> as side effects</li>
          <li><b>Diagnostic use:</b> The <b class="purple-term">metyrapone test</b> — metyrapone is given overnight; in a normal person, falling cortisol → ACTH rises → 11-deoxycortisol rises (a testable upstream metabolite). Failure of ACTH to rise suggests hypothalamic-pituitary disease (secondary adrenal insufficiency).</li>
          <li><b>Therapeutic use:</b> Cushing's syndrome (medical management), preparation for adrenalectomy</li>
        </ul>

        <h3>Ketoconazole</h3>
        <p><b class="purple-term">Ketoconazole</b> is an antifungal that also inhibits multiple cytochrome P450 enzymes involved in steroidogenesis, including <b>17α-hydroxylase</b>, <b>11β-hydroxylase</b>, and <b>cholesterol side-chain cleavage</b> — reducing cortisol, androgens and oestrogens.</p>
        <ul>
          <li><b>Use:</b> Cushing's syndrome (particularly ectopic ACTH syndrome), sometimes used off-label for prostate cancer</li>
          <li><b>Side effects:</b> Hepatotoxicity (monitor LFTs), gynaecomastia (inhibits testosterone synthesis), drug interactions (inhibits CYP3A4)</li>
        </ul>

        <h3>Osilodrostat</h3>
        <p><b class="purple-term">Osilodrostat</b> is a newer, potent <b>11β-hydroxylase inhibitor</b> (same target as metyrapone but more selective and more potent). It is licensed for Cushing's disease.</p>

        <h3>Mitotane</h3>
        <p><b class="purple-term">Mitotane</b> is an <b>adrenolytic</b> drug — it directly destroys adrenocortical cells. It is structurally related to the insecticide DDT.</p>
        <ul>
          <li><b>Use:</b> <b class="purple-term">Adrenocortical carcinoma</b> (primary or adjuvant after surgery) — the only drug licensed for this indication</li>
          <li><b>Effect:</b> Causes adrenal insufficiency → patients require steroid replacement (hydrocortisone + fludrocortisone)</li>
          <li><b>Side effects:</b> Severe nausea/vomiting, hepatotoxicity, neurological effects (ataxia, confusion)</li>
        </ul>
        <div class="highlight-box">
          <b>Summary — steroidogenesis inhibitors:</b><br/>
          Metyrapone → blocks 11β-hydroxylase → used in Cushing's + diagnostic metyrapone test<br/>
          Ketoconazole → blocks multiple CYP450 steps → broad cortisol/androgen reduction; hepatotoxic<br/>
          Osilodrostat → selective 11β-hydroxylase inhibitor → Cushing's disease<br/>
          Mitotane → adrenolytic → adrenocortical carcinoma only
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with Cushing's syndrome secondary to an inoperable adrenal adenoma is started on metyrapone. Which enzyme does metyrapone inhibit, and what upstream metabolite will accumulate?",
      options: [
        "17α-hydroxylase — causing accumulation of progesterone",
        "11β-hydroxylase — causing accumulation of 11-deoxycortisol",
        "21-hydroxylase — causing accumulation of 17-hydroxyprogesterone",
        "Aromatase — causing accumulation of testosterone",
      ],
      correct: 1,
      explanation:
        "Metyrapone specifically inhibits 11β-hydroxylase, which normally converts 11-deoxycortisol → cortisol. Blocking this final step causes cortisol to fall and 11-deoxycortisol to accumulate. The rising ACTH (from loss of negative feedback) drives further upstream steroidogenesis, including mineralocorticoid precursors (causing hypertension) and androgens (causing hirsutism). Monitoring 11-deoxycortisol levels can confirm drug efficacy.",
    },
    {
      type: "question",
      text: "A 58-year-old patient is diagnosed with adrenocortical carcinoma with hepatic metastases, making surgical cure impossible. Which drug is specifically licensed for this condition?",
      options: [
        "Metyrapone",
        "Spironolactone",
        "Mitotane",
        "Ketoconazole",
      ],
      correct: 2,
      explanation:
        "Mitotane (o,p'-DDD) is the only drug specifically licensed for adrenocortical carcinoma. It is adrenolytic — it selectively destroys adrenocortical tissue — and is used both adjuvantly after resection and for unresectable or metastatic disease. Because it destroys the adrenal cortex, patients develop adrenal insufficiency and require replacement with hydrocortisone and fludrocortisone. Mitotane has significant toxicity (GI effects, hepatotoxicity, CNS effects) and narrow therapeutic window.",
    },
    {
      type: "text",
      content: `
        <h3>Phaeochromocytoma — Pharmacological Management</h3>
        <p>Phaeochromocytoma is a catecholamine-secreting tumour of the adrenal medulla. Surgical resection (adrenalectomy) is the definitive treatment, but <b>adequate pre-operative pharmacological preparation is essential</b> — uncontrolled catecholamine release during surgery can cause fatal hypertensive crises or arrhythmias.</p>

        <h3>Alpha blockade MUST come before beta blockade</h3>
        <p>This is the most tested pharmacological principle in phaeochromocytoma management:</p>
        <ol>
          <li><b>Step 1 — Alpha blockade first</b> (at least 10–14 days pre-operatively):
            <ul>
              <li><b class="purple-term">Phenoxybenzamine</b>: irreversible, non-selective α₁ and α₂ blocker — the traditional choice. Causes vasodilatation → allows volume expansion to correct the "contracted" intravascular volume typical of phaeochromocytoma.</li>
              <li><b>Doxazosin</b> (selective α₁ blocker, reversible): increasingly preferred — shorter acting, fewer side effects, easier to manage perioperatively.</li>
            </ul>
          </li>
          <li><b>Step 2 — Beta blockade AFTER alpha established</b>:
            <ul>
              <li><b class="purple-term">Propranolol</b> (non-selective β blocker) or <b>atenolol</b> (selective β₁) — added after adequate alpha blockade to control reflex tachycardia caused by the vasodilation.</li>
            </ul>
          </li>
        </ol>
        <div class="mnemonic-card">
          <b>Alpha before Beta — always.</b><br/>
          If you give beta blockers FIRST → you block β₂ vasodilation in skeletal muscle, leaving α₁ vasoconstriction unopposed → <b>severe hypertensive crisis</b> and potentially fatal surge in blood pressure.
        </div>

        <h3>Intraoperative management</h3>
        <p>During adrenalectomy, tumour handling causes catecholamine surges. These are managed with:</p>
        <ul>
          <li><b class="purple-term">Phentolamine</b> (IV alpha blocker) — for acute hypertensive crises on the table</li>
          <li><b>Sodium nitroprusside</b> — fast-acting vasodilator for severe intraoperative hypertension</li>
          <li>Once the tumour is removed, the major risk shifts to <b>hypotension</b> (sudden loss of catecholamine tone + residual alpha blockade) → IV fluids + vasopressors may be needed</li>
        </ul>

        <h3>Metyrosine — reducing catecholamine synthesis</h3>
        <p><b class="purple-term">Metyrosine</b> (α-methyl-L-tyrosine) inhibits <b class="purple-term">tyrosine hydroxylase</b> — the rate-limiting enzyme in catecholamine synthesis (Tyrosine → L-DOPA). By blocking this step, it reduces total catecholamine production by 35–80%.</p>
        <ul>
          <li><b>Use:</b> Pre-operative preparation in phaeochromocytoma (particularly for large or malignant tumours), or in patients unfit for surgery</li>
          <li><b>Side effects:</b> Sedation, extrapyramidal effects (it also reduces dopamine synthesis), crystalluria</li>
        </ul>
      `,
    },
    {
      type: "question",
      text: "A patient with a known phaeochromocytoma is scheduled for laparoscopic adrenalectomy in 2 weeks. Pre-operative preparation begins with phenoxybenzamine. Why is it essential to establish alpha blockade before adding propranolol?",
      options: [
        "Propranolol increases ACTH release, which stimulates further catecholamine production",
        "Beta blockade without prior alpha blockade leaves peripheral alpha-mediated vasoconstriction unopposed, risking a severe hypertensive crisis",
        "Phenoxybenzamine sensitises beta receptors to the effects of propranolol",
        "Propranolol blocks catecholamine synthesis at the level of tyrosine hydroxylase",
      ],
      correct: 1,
      explanation:
        "Catecholamines (especially adrenaline) cause both α₁ vasoconstriction and β₂ vasodilation. Beta-2 vasodilation partially offsets the α₁ vasoconstrictive surge. If you block β receptors first, you eliminate the β₂ vasodilatory counterbalance — leaving pure α₁ vasoconstriction unopposed → catastrophic hypertensive crisis. Establishing alpha blockade first protects against this. Only then can propranolol be safely added to control the reflex tachycardia that results from vasodilation.",
    },
    {
      type: "question",
      text: "During surgery to remove a phaeochromocytoma, the patient's blood pressure suddenly rises to 230/130 mmHg as the surgeon handles the tumour. What is the most appropriate immediate drug?",
      options: [
        "IV propranolol to control the tachycardia",
        "IV phentolamine or sodium nitroprusside for acute alpha blockade / vasodilation",
        "IV hydrocortisone in case of adrenal insufficiency",
        "IV fludrocortisone to counteract mineralocorticoid loss",
      ],
      correct: 1,
      explanation:
        "Tumour manipulation causes acute catecholamine release → acute hypertensive crisis. This is managed with IV phentolamine (a short-acting non-selective alpha blocker) or IV sodium nitroprusside (a direct vasodilator), both of which rapidly lower blood pressure. IV propranolol would be dangerous without adequate alpha blockade in place. Hydrocortisone and fludrocortisone are needed after tumour removal (to cover for possible contralateral adrenal insufficiency), not during a hypertensive surge.",
    },
    {
      type: "flashcard",
      front: "What are the key pharmacological steps in preparing a patient with phaeochromocytoma for surgery?",
      back: `<ol style="margin:0; padding-left:18px; font-size:0.9em; line-height:1.7">
        <li><b>Alpha blockade FIRST</b> (10–14 days pre-op)<br/>
          Phenoxybenzamine (irreversible, non-selective) OR doxazosin (reversible, α₁-selective)<br/>
          → Vasodilatation → allow volume expansion (high Na⁺ diet + IV fluids)</li>
        <li><b>Beta blockade AFTER alpha established</b><br/>
          Propranolol or atenolol → controls reflex tachycardia<br/>
          ⚠️ NEVER beta first → unopposed α₁ → hypertensive crisis</li>
        <li><b>Intraoperative surges</b>: IV phentolamine or sodium nitroprusside</li>
        <li><b>Post-tumour removal</b>: expect hypotension → IV fluids ± vasopressors<br/>
          Monitor glucose (catecholamines suppressed insulin — hypoglycaemia can occur)</li>
        <li><b>Optional</b>: metyrosine (tyrosine hydroxylase inhibitor) to reduce catecholamine synthesis</li>
      </ol>`,
      hint: "Alpha → Beta. Never Beta first. Phentolamine for intraoperative crises. Watch for hypoglycaemia after removal.",
    },
    {
      type: "flashcard",
      front: "Summarise the main drug classes used in adrenal pharmacology, their targets and key examples.",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.85em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 6px; text-align:left">Drug class</th>
          <th style="padding:4px 6px">Examples</th>
          <th style="padding:4px 6px">Target / mechanism</th>
          <th style="padding:4px 6px">Key use</th>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Glucocorticoids</b></td>
          <td style="padding:4px 6px; text-align:center">Hydrocortisone, Prednisolone, Dexamethasone</td>
          <td style="padding:4px 6px; text-align:center">GR agonist (mimic cortisol)</td>
          <td style="padding:4px 6px; text-align:center">Anti-inflammatory, replacement, cerebral oedema</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Mineralocorticoid</b></td>
          <td style="padding:4px 6px; text-align:center">Fludrocortisone</td>
          <td style="padding:4px 6px; text-align:center">MR agonist (mimic aldosterone)</td>
          <td style="padding:4px 6px; text-align:center">Addison's disease, postural hypotension</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>MR antagonists</b></td>
          <td style="padding:4px 6px; text-align:center">Spironolactone, Eplerenone</td>
          <td style="padding:4px 6px; text-align:center">Block aldosterone receptor</td>
          <td style="padding:4px 6px; text-align:center">Conn's, heart failure, resistant HTN</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Steroidogenesis inhibitors</b></td>
          <td style="padding:4px 6px; text-align:center">Metyrapone, Ketoconazole, Osilodrostat, Mitotane</td>
          <td style="padding:4px 6px; text-align:center">Block CYP450 enzymes / adrenolytic</td>
          <td style="padding:4px 6px; text-align:center">Cushing's syndrome; adrenocortical carcinoma (mitotane)</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Alpha blockers</b></td>
          <td style="padding:4px 6px; text-align:center">Phenoxybenzamine, Doxazosin, Phentolamine</td>
          <td style="padding:4px 6px; text-align:center">Block α₁ (± α₂) adrenoceptors</td>
          <td style="padding:4px 6px; text-align:center">Phaeochromocytoma pre-op (first step)</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Catecholamine synthesis inhibitor</b></td>
          <td style="padding:4px 6px; text-align:center">Metyrosine</td>
          <td style="padding:4px 6px; text-align:center">Inhibits tyrosine hydroxylase</td>
          <td style="padding:4px 6px; text-align:center">Phaeochromocytoma (reduce synthesis)</td>
        </tr>
      </table>`,
      hint: "GR agonists = glucocorticoids | MR agonist = fludrocortisone | MR antagonists = spironolactone/eplerenone | 11β-OHase = metyrapone | Adrenolytic = mitotane",
    },
  ],
};
